import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import {
  useGetCampusQuery,
  useGetStaffByEmailIdQuery,
  useGetAssignmentByClusterQuery,
  useGetAllAssignmentQuestionQuery,
} from "../../../../graphql/generated.tsx";
import AdminLayout from "../../Layout/index.jsx";
import LightSpinner from "../../../../components/LightSpinner";
import { Table, Button, Modal } from "flowbite-react";
import { IoMdArrowRoundBack } from "react-icons/io";
import { FaDownload } from "react-icons/fa6";
import { MdTableChart } from "react-icons/md";
import {
  AiOutlineBarChart,
  AiOutlineCalendar,
  AiOutlineClockCircle,
  AiOutlineFileText,
} from "react-icons/ai";
import { HiUsers } from "react-icons/hi2";

const GET_STUDENTS_RESULTS = gql`
  query getStudentsResults(
    $campusId: String!
    $searchTerm: String
    $page: Int
    $limit: Int
    $clusterid: String
  ) {
    getStudentsResults(
      campusId: $campusId
      searchTerm: $searchTerm
      page: $page
      limit: $limit
      clusterid: $clusterid
    ) {
      students {
        id
        name
        email
        rollNo
          department
        assignmentDetails {
          assignment_id
          totalScore
          cheat_reasons
        }
      }
    }
  }
`;

const GET_ASSIGNMENT_SUBMIT_INLINE = gql`
  query GetAssignmentSubmitInline(
    $assignmentId: String!
    $studentId: String!
    $campus_name: String!
  ) {
    getAssignmentSubmit(
      assignmentId: $assignmentId
      studentId: $studentId
      campus_name: $campus_name
    ) {
      id
      totalScore
      completed
      endTime
      cheat_count
      cheat_reasons
      tabSwitches
      questions {
        question_id
        answer
        score
        language
      }
    }
  }
`;

const GET_ASSIGNMENT_QUESTIONS = gql`
  query GetAllAssignmentQuestionInline($assignmentId: String!) {
    getAllAssignmentQuestion(assignmentId: $assignmentId) {
      id
      title
      text
      question_sub_text
      test_cases {
        input
        output
        visibility
      }
    }
  }
`;

const SubmissionCodeViewer = ({ code, language }) => {
  const [languageExtension, setLanguageExtension] = useState([]);
  const sourceCode = code || "// No answer submitted";

  useEffect(() => {
    let active = true;
    const loadLanguage = async () => {
      const normalizedLanguage = String(language || "").toUpperCase();
      let extension = null;

      try {
        if (["CPP", "C", "C++"].includes(normalizedLanguage)) {
          const { cpp } = await import("@codemirror/lang-cpp");
          extension = cpp();
        } else if (["PYTHON", "PY"].includes(normalizedLanguage)) {
          const { python } = await import("@codemirror/lang-python");
          extension = python();
        } else if (normalizedLanguage === "JAVA") {
          const { java } = await import("@codemirror/lang-java");
          extension = java();
        } else if (["JAVASCRIPT", "JS"].includes(normalizedLanguage)) {
          const { javascript } = await import("@codemirror/lang-javascript");
          extension = javascript();
        }
      } catch {
        extension = null;
      }

      if (active) setLanguageExtension(extension ? [extension] : []);
    };

    loadLanguage();
    return () => {
      active = false;
    };
  }, [language]);

  const editorTheme = EditorView.theme({
    "&": { fontSize: "16px" },
    ".cm-content": { padding: "12px 0" },
    ".cm-gutters": { fontSize: "14px" },
  });

  return (
    <CodeMirror
      value={sourceCode}
      extensions={[...languageExtension, editorTheme]}
      theme="light"
      editable={false}
      readOnly
      minHeight="380px"
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        highlightActiveLine: false,
      }}
      className="submission-code-editor"
    />
  );
};

const AssignmentScores = () => {
  const { assignmentId } = useParams();
  const [searchParams] = useSearchParams();
  const clusterId = searchParams.get("clusterId");
  const navigate = useNavigate();

  const email = JSON.parse(localStorage.getItem("user"))?.email;

  const { data: staffData, loading: staffLoading } =
    useGetStaffByEmailIdQuery({
      variables: { email },
    });

  const { data: campusData, loading: campusLoading } = useGetCampusQuery({
    variables: { id: staffData?.getStaffByEmailId?.campusId },
    skip: staffLoading,
  });

  const campus = campusData?.campus;

  const { data: studentData, loading: studentLoading } = useQuery(
    GET_STUDENTS_RESULTS,
    {
      variables: {
        campusId: campus?.id,
        clusterid: clusterId,
        page: 1,
        limit: 3000,
      },
      skip: campusLoading,
    }
  );

  const { data: assignmentData, loading: assignmentLoading } =
    useGetAssignmentByClusterQuery({
      variables: {
        campusName: campus?.name,
        clusterId: clusterId,
      },
      skip: studentLoading,
    });

  const assignment = assignmentData?.getAssignmentByCluster?.find(
    (a) => a.id === assignmentId
  );

  const [showModal, setShowModal] = useState(false);
  const [selectedCheatReasons, setSelectedCheatReasons] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [assignmentTotalMarks, setAssignmentTotalMarks] = useState(0);
  const [openedRow, setOpenedRow] = useState(null);
  const [openedStudentName, setOpenedStudentName] = useState("");
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);

  const [loadSubmission, { data: submissionData, loading: submissionLoading }] =
    useLazyQuery(GET_ASSIGNMENT_SUBMIT_INLINE);

  const [loadSubmissionQuestions, { data: submissionQuestionsData }] =
    useLazyQuery(GET_ASSIGNMENT_QUESTIONS);

  const submissionQuestionMap = useMemo(() => {
    const map = {};
    (submissionQuestionsData?.getAllAssignmentQuestion || []).forEach((q) => {
      map[q.id] = q;
    });
    return map;
  }, [submissionQuestionsData]);

  // Fetch assignment questions to calculate total marks
  const { data: assignmentQuestionsData, loading: questionsLoading } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId, limit: 1000 },
    skip: !assignmentId,
  });

  // Calculate total marks when questions are loaded
  useEffect(() => {
    if (assignmentQuestionsData?.getAllAssignmentQuestion) {
      const questions = assignmentQuestionsData.getAllAssignmentQuestion;
      const totalMarks = questions.reduce((total, question) => {
        const marks = Number(question.marks);
        return total + (Number.isFinite(marks) ? marks : 0);
      }, 0);
      setAssignmentTotalMarks(totalMarks);
    }
  }, [assignmentQuestionsData]);

  const students = studentData?.getStudentsResults?.students || [];

  const studentsWithSubmission = useMemo(() => {
    return students.filter((student) => {
      const details = student.assignmentDetails?.find(
        (d) => d.assignment_id === assignmentId
      );

      // Treat as "submitted" only when we actually have an entry for this assignment
      // and the score value exists (0 is valid).
      return Boolean(details?.assignment_id) && details?.totalScore != null;
    });
  }, [students, assignmentId]);

  const visibleStudents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return studentsWithSubmission;

    return studentsWithSubmission.filter((student) => {
      const name = (student?.name || "").toLowerCase();
      const rollNo = (student?.rollNo || "").toLowerCase();
      return name.includes(term) || rollNo.includes(term);
    });
  }, [studentsWithSubmission, searchTerm]);

  const totalStudents = studentsWithSubmission.length;
  const averageScore =
    totalStudents > 0
      ? Math.round(
          studentsWithSubmission.reduce(
            (sum, student) =>
              sum + (student.assignmentDetails?.find(
                (d) => d.assignment_id === assignmentId
              )?.totalScore ?? 0),
            0
          ) / totalStudents
        )
      : 0;

  const durationValue = assignment?.duration;
  const durationLabel = Number.isFinite(durationValue)
    ? `${Math.round(durationValue)} hr`
    : "N/A";

  const totalQuestions =
    assignment?.totalQuestions ??
    assignment?.totalNumberOfQuestion ??
    assignmentQuestionsData?.getAllAssignmentQuestion?.length ??
    "N/A";

  const totalMarks = assignmentQuestionsData?.getAllAssignmentQuestion?.length
    ? assignmentQuestionsData.getAllAssignmentQuestion.reduce((sum, question) => {
        const marks = Number(question.marks);
        return sum + (Number.isFinite(marks) ? marks : 0);
      }, 0)
    : "N/A";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const reportDate = formatDate(assignment?.due_date || assignment?.createdAt);

  if (staffLoading || campusLoading || studentLoading || assignmentLoading || questionsLoading) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-screen gap-2">
          <LightSpinner message={"loading assignment report"}/>
         
        </div>
      </AdminLayout>
    );
  }

  // CSV DOWNLOAD FUNCTION
  const handleDownloadCSV = () => {
    const rows = studentsWithSubmission.map((student) => {
      const details = student.assignmentDetails?.find(
        (d) => d.assignment_id === assignmentId
      );

      const reasons = details?.cheat_reasons
        ?.split("\n")
        .filter((line) => !line.startsWith("null"))
        .map((line) => line.split("|")[0].trim())
        .join(", ");

      return {
        Name: student.name,
        Roll_Number: student.rollNo,
          Department: student.department || "-",
        Score: details?.totalScore ?? "-",
        Malpractice: reasons || "-",
      };
    });

    if (rows.length === 0) return;

    const header = Object.keys(rows[0] || {}).join(",") + "\n";

    const csvContent =
      header +
      rows
        .map((r) =>
          Object.values(r)
            .map((val) => `"${(val || "").toString().replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `${assignment?.name || "Assignment"}_Scores.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewCheatReasons = (cheatReasons) => {
    setSelectedCheatReasons(cheatReasons);
    setShowModal(true);
  };

  const handleViewNotAttended = () => {
    const notAttendedStudents = students.filter((s) => {
      const details = s.assignmentDetails?.find(
        (d) => d.assignment_id === assignmentId
      );

      const hasSubmission = Boolean(details?.assignment_id) && details?.totalScore != null;
      return !hasSubmission;
    });

    navigate("/admin/assignment-report/notAttendee", {
      state: { notAttendedStudents },
    });
  };

  const handleViewSubmission = (studentId, studentName) => {
    if (!assignmentId || !studentId || !campus?.name) return;

    setOpenedRow(studentId);
    setOpenedStudentName(studentName || "");
    setCurrentQIndex(0);
    setShowQuestion(false);

    loadSubmission({
      variables: {
        assignmentId,
        studentId,
        campus_name: campus.name,
      },
    });

    loadSubmissionQuestions({
      variables: { assignmentId },
    });
  };

  const handleCloseSubmissionModal = () => {
    setOpenedRow(null);
    setOpenedStudentName("");
    setCurrentQIndex(0);
    setShowQuestion(false);
  };

  return (
    <AdminLayout>
      <div className="min-h-screen px-4 pt-5 pb-8 bg-white rounded-lg shadow-lg sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-4 p-5 mb-6 sm:mb-8">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center">
              <button onClick={() => window.history.back()}>
                <IoMdArrowRoundBack className="w-5 h-5 text-gray-600 sm:w-6 sm:h-6" />
              </button>
              <div className="ml-2 text-lg font-bold sm:text-xl md:text-2xl">
                {assignment?.name || "Assignment"}
              </div>
            </div>

            <div className="flex flex-col w-full gap-2 sm:flex-row sm:w-auto sm:items-center">
              {/* Search Bar */}
              <div className="relative w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search by name or roll number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg sm:w-80 focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-base"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>

              <button
                onClick={handleViewNotAttended}
                className="px-3 py-2 text-sm text-white rounded sm:px-4 sm:text-base bg-primary whitespace-nowrap"
              >
                View Not Attended
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowDownloadDropdown((prev) => !prev)}
                  className="inline-flex items-center justify-center w-9 h-9 text-slate-600 transition-all hover:text-slate-900 focus:outline-none"
                  title="Download"
                  aria-label="Download"
                >
                  <FaDownload size={20} />
                </button>

                {showDownloadDropdown && (
                  <div className="absolute top-0 right-[calc(100%+10px)] z-20 w-[165px] rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_10px_24px_rgba(2,6,23,0.08)]">
                    <p className="mb-2 px-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                      Choose format
                    </p>
                    <button
                      onClick={() => {
                        handleDownloadCSV();
                        setShowDownloadDropdown(false);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-slate-700 transition-colors hover:bg-slate-100"
                    >
                      <span className="rounded-lg bg-slate-100 p-1.5 text-slate-600">
                        <MdTableChart size={17} />
                      </span>
                      <span className="text-sm font-medium">CSV</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
          <div className="grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-2 sm:divide-y-0 sm:divide-x lg:grid-cols-6">
            <div className="flex items-center gap-3 px-6 py-4 text-left">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                <AiOutlineClockCircle className="w-5 h-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  Duration
                </p>
                <p className="text-base font-bold text-gray-900">
                  {durationLabel}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 text-left">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                <AiOutlineFileText className="w-5 h-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  Questions
                </p>
                <p className="text-base font-bold text-gray-900">
                  {totalQuestions}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 text-left">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                <AiOutlineCalendar className="w-5 h-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  Due Date
                </p>
                <p className="text-base font-bold text-gray-900 whitespace-nowrap">
                  {reportDate}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 text-left">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                <HiUsers className="w-5 h-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  Students
                </p>
                <p className="text-base font-bold text-gray-900">
                  {totalStudents}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 text-left">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                <AiOutlineBarChart className="w-5 h-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  Total Marks
                </p>
                <p className="text-base font-bold text-gray-900">
                  {totalMarks}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 px-6 py-4 text-left">
              <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                <span className="text-base font-bold text-slate-600">★</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  Avg Score
                </p>
                <p className="text-base font-bold text-gray-900">
                  {averageScore}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
        <div className="p-5 -mx-4 overflow-x-auto sm:mx-0">
          <Table className="min-w-[600px]">
            <Table.Head style={{backgroundColor: '#5c7a94'}}>
              <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-1.5 text-[10px] text-center text-white sm:px-3 sm:py-2 sm:text-xs">
                S.No
              </Table.HeadCell>
              <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-1.5 text-[10px] text-center text-white sm:px-3 sm:py-2 sm:text-xs">
                Name
              </Table.HeadCell>
              <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-1.5 text-[10px] text-center text-white sm:px-3 sm:py-2 sm:text-xs">
                Roll No
              </Table.HeadCell>
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-1.5 text-[10px] text-center text-white sm:px-3 sm:py-2 sm:text-xs">
                  Department
                </Table.HeadCell>
              <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-1.5 text-[10px] text-center text-white sm:px-3 sm:py-2 sm:text-xs">
                <div>
                  <div>Score</div>
                  {assignmentTotalMarks > 0 && (
                    <div className="text-[8px] sm:text-[10px] text-white/80 font-semibold">(out of {assignmentTotalMarks})</div>
                  )}
                </div>
              </Table.HeadCell>
              <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-1.5 text-[10px] text-center text-white sm:px-3 sm:py-2 sm:text-xs">
                Cheat Reasons
              </Table.HeadCell>
              <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-1.5 text-[10px] text-center text-white sm:px-3 sm:py-2 sm:text-xs">
                Submission
              </Table.HeadCell>
            </Table.Head>

            <Table.Body>
              {visibleStudents.length === 0 ? (
                <Table.Row>
                  <Table.Cell
                    colSpan={7}
                    className="px-4 py-10 text-sm text-center text-gray-500"
                  >
                    {studentsWithSubmission.length === 0
                      ? "No student submission."
                      : "No matching students."}
                  </Table.Cell>
                </Table.Row>
              ) : (
                visibleStudents.map((student, idx) => {
                  const details = student.assignmentDetails?.find(
                    (d) => d.assignment_id === assignmentId
                  );
                  const cheatReasons = details?.cheat_reasons || "";

                  return (
                    <Table.Row
                      key={student.id}
                      className={`transition duration-150 ease-in-out hover:bg-blue-50/60 hover:shadow-md ${
                        idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <Table.Cell className="px-2 py-2 text-xs font-medium text-center text-gray-700 sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base">
                        {idx + 1}
                      </Table.Cell>
                      <Table.Cell className="px-2 py-2 text-xs text-center sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base whitespace-nowrap">
                        {student.name}
                      </Table.Cell>
                      <Table.Cell className="px-2 py-2 text-xs text-center sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base whitespace-nowrap">
                        {student.rollNo}
                      </Table.Cell>
                        <Table.Cell className="px-2 py-2 text-xs text-center sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base">
                          {student.department && String(student.department).trim() !== "" ? student.department : "-"}
                        </Table.Cell>
                      <Table.Cell className="px-2 py-2 text-xs text-center sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base">
                        {details?.totalScore ?? "-"}
                      </Table.Cell>

                      <Table.Cell className="px-2 py-2 text-center sm:px-4 md:px-6 sm:py-3">
                        {cheatReasons ? (
                          <span
                            className="text-xs font-semibold text-blue-600 cursor-pointer select-none hover:text-blue-800 hover:underline sm:text-sm md:text-base"
                            onClick={() => handleViewCheatReasons(cheatReasons)}
                          >
                            View
                          </span>
                        ) : (
                          "-"
                        )}
                      </Table.Cell>

                      <Table.Cell className="px-2 py-2 text-center sm:px-4 md:px-6 sm:py-3">
                        <span
                          className="text-xs font-semibold text-blue-600 cursor-pointer select-none hover:text-blue-800 hover:underline sm:text-sm md:text-base"
                          onClick={() => handleViewSubmission(student.id, student.name)}
                        >
                          View
                        </span>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table>
        </div>
        </div>
      </div>

      {openedRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[2px]"
          onClick={handleCloseSubmissionModal}
        >
          <div
            className="relative bg-white shadow-2xl w-full h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#0F537E] to-[#1a6a99] px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0">
              <h3 className="max-w-xs text-lg font-semibold text-white truncate" title={assignment?.name || "Submission Details"}>
                {assignment?.name?.length > 30
                  ? `${assignment.name.slice(0, 30)}...`
                  : assignment?.name || "Submission Details"}
              </h3>
              <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end sm:gap-4">
                {!submissionLoading && submissionData?.getAssignmentSubmit && (
                  <div className="flex flex-wrap items-center gap-2 text-sm sm:gap-3 text-white/90">
                    {submissionData.getAssignmentSubmit.endTime && (
                      <span className="hidden sm:inline">
                        Submitted: {" "}
                        {new Date(submissionData.getAssignmentSubmit.endTime).toLocaleString("en-US", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        submissionData.getAssignmentSubmit.completed
                          ? "bg-green-400/30 text-green-100"
                          : "bg-yellow-400/30 text-yellow-100"
                      }`}
                    >
                      {submissionData.getAssignmentSubmit.completed ? "Completed" : "Incomplete"}
                    </span>
                    <span className="font-semibold">
                      Score: {submissionData.getAssignmentSubmit.totalScore ?? 0}
                    </span>
                    <span className="font-medium text-white/80">
                      {openedStudentName}
                    </span>
                  </div>
                )}
                <button
                  onClick={handleCloseSubmissionModal}
                  className="ml-2 text-xl leading-none transition text-white/80 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="flex-1 p-4 sm:p-6 space-y-5 overflow-y-auto">
              {submissionLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F537E]"></div>
                  <p className="ml-3 text-[#0F537E] font-medium">Loading submission details...</p>
                </div>
              ) : submissionData?.getAssignmentSubmit ? (
                <>
                  {submissionData.getAssignmentSubmit.questions?.length > 0 ? (
                    (() => {
                      const questions = submissionData.getAssignmentSubmit.questions;
                      const q = questions[currentQIndex];
                      return (
                        <div className="overflow-hidden border border-gray-200 rounded-xl">
                          <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                              </svg>
                              <span className="text-sm font-semibold text-gray-700">
                                Q{currentQIndex + 1}
                                {submissionQuestionMap[q.question_id]?.title
                                  ? ` - ${submissionQuestionMap[q.question_id].title}`
                                  : ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => setShowQuestion((v) => !v)}
                                className="px-3 py-1 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
                              >
                                {showQuestion ? "View Code" : "View Question"}
                              </button>
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono">
                                {q.language || "Unknown"}
                              </span>
                              <span className="text-xs font-semibold text-green-700">
                                Score: {q.score}
                              </span>
                            </div>
                          </div>
                          {showQuestion ? (
                            <div className="p-5 bg-gray-50">
                              {submissionQuestionMap[q.question_id]?.title ? (
                                <div className="mb-3 text-base font-semibold text-gray-900">
                                  {submissionQuestionMap[q.question_id].title}
                                </div>
                              ) : null}
                              {submissionQuestionMap[q.question_id]?.question_sub_text ? (
                                <pre className="mb-4 overflow-x-auto text-sm text-gray-700 whitespace-pre-wrap break-words">
                                  {submissionQuestionMap[q.question_id].question_sub_text}
                                </pre>
                              ) : null}
                              {submissionQuestionMap[q.question_id]?.text ? (
                                <div
                                  className="text-sm leading-relaxed text-gray-900"
                                  dangerouslySetInnerHTML={{
                                    __html: submissionQuestionMap[q.question_id].text,
                                  }}
                                />
                              ) : (
                                <div className="text-sm text-gray-600">
                                  Question details not available.
                                </div>
                              )}

                            </div>
                          ) : (
                            <div className="bg-white">
                              <SubmissionCodeViewer
                                code={q.answer?.trim()}
                                language={q.language}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="py-6 text-sm text-center text-gray-500">
                      No final answers submitted for this assignment.
                    </div>
                  )}
                </>
              ) : (
                <div className="py-12 text-center">
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="font-medium text-gray-500">No submission found.</p>
                </div>
              )}
            </div>

            {!submissionLoading &&
              submissionData?.getAssignmentSubmit?.questions?.length > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t shrink-0 bg-gray-50">
                  <button
                    disabled={currentQIndex === 0}
                    onClick={() => setCurrentQIndex((i) => i - 1)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                      currentQIndex === 0
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    ← Prev
                  </button>

                  <span className="text-sm font-medium text-gray-500">
                    {currentQIndex + 1} / {submissionData.getAssignmentSubmit.questions.length}
                  </span>

                  <button
                    disabled={
                      currentQIndex ===
                      submissionData.getAssignmentSubmit.questions.length - 1
                    }
                    onClick={() => setCurrentQIndex((i) => i + 1)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                      currentQIndex ===
                      submissionData.getAssignmentSubmit.questions.length - 1
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    Next →
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal show={showModal} size="2xl" onClose={() => setShowModal(false)}>
        <Modal.Header>Malpractice</Modal.Header>
        <Modal.Body>
          <div className="overflow-x-auto">
            <Table className="text-xs text-gray-500 sm:text-sm">
              <Table.Head style={{backgroundColor: '#5c7a94'}}>
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-2 text-xs text-center text-white sm:text-sm sm:px-4">
                  Reason
                </Table.HeadCell>
                <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="px-2 py-2 text-xs text-center text-white sm:text-sm sm:px-4">
                  Timestamp
                </Table.HeadCell>
              </Table.Head>

              <Table.Body className="divide-y divide-gray-200">
                {selectedCheatReasons
                  .split("\n")
                  .filter((line) => !line.startsWith("null"))
                  .map((line, index) => {
                    const [reason, timestamp] = line
                      .split("|")
                      .map((s) => s?.trim());

                    return (
                      <Table.Row key={index}>
                        <Table.Cell className="px-2 py-2 text-xs text-center sm:px-4 sm:text-sm">
                          {reason}
                        </Table.Cell>
                        <Table.Cell className="px-2 py-2 text-xs text-center sm:px-4 sm:text-sm">
                          {timestamp}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
              </Table.Body>
            </Table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button color="gray" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </AdminLayout>
  );
};

export default AssignmentScores;
